import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Modal,
  TablePagination,
  Button,
  Typography,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";

import { deleteFaqService, getFaqService } from "../../services/faq.service";
import { boxStyle, configToast, modalStyle } from "../../constants";
import { StyledTableCell, StyledTableRow } from "../../shared/style";
import DeleteModal from "../../components/modals/DeleteModal";
import { useAppDispatch } from "../../state/hook";
import { setApp } from "../../state/libs/appSlice";

const contentToolbar = {
  toolbar: {
    title: "การตั้งค่าเว็บไซต์",
    subtitles: ["จัดการคำถามที่พบบ่อย"],
  },
};

const FaqList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [faqList, setFaqList] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, getValues } = useForm({
    defaultValues: {
      searchText: "",
    },
  });

  const [openWarning, setWarningOpen] = useState<boolean>(false);
  const [faqId, setFaqId] = useState<number | null>(null);

  const handleWarningOpen = (id: number) => {
    setFaqId(id);
    setWarningOpen(true);
  };

  const handleWarningClose = () => {
    setFaqId(null);
    setWarningOpen(false);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleConfirmDelete = async () => {
    try {
      const id = faqId as number;
      handleWarningClose();
      await deleteFaqService(id);
      getFaq();
      toast.success("ลบคำถามที่พบบ่อยสำเร็จ", configToast);
    } catch (error) {
      toast.error("ลบคำถามที่พบบ่อยไม่สำเร็จ", configToast);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOnSearch = () => {
    getFaq();
  };

  const onKeyPress = (e: any) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleOnSearch();
    }
  };

  const getFaq = () => {
    getFaqService({ size: rowsPerPage, page: page + 1, ...getValues() }).then(
      (resp) => {
        setFaqList(resp.data.items);
        setCount(resp.data.total_row);
      }
    );
  };

  const handleNavigateEditFaq = (faqId: string) => {
    navigate(`/edit-faq/${faqId}`);
  };

  const setContentToolbar = () => {
    dispatch(setApp(contentToolbar));
  };

  useEffect(getFaq, [getValues, page, rowsPerPage]);

  useEffect(setContentToolbar, [dispatch]);

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box sx={boxStyle}>
        <Box display="flex" justifyContent="space-between" gap="16px">
          <Box>
            <TextField
              fullWidth
              type="text"
              placeholder="ค้นหา"
              onKeyUp={onKeyPress}
              {...register("searchText")}
            />
          </Box>

          <Box>
            <Button
              variant="contained"
              startIcon={<SearchIcon sx={{ color: "white" }} />}
              color="green"
              onClick={handleOnSearch}
            >
              <Typography color="white">ค้นหา</Typography>
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={boxStyle}>
        <Box display="flex" gap="8px">
          <Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon sx={{ color: "white" }} />}
              color="green"
              component={RouterLink}
              to="/create-faq"
            >
              <Typography color="white">สร้าง</Typography>
            </Button>
          </Box>
        </Box>

        <TableContainer
          component={Paper}
          sx={{ boxShadow: "none", borderRadius: 0 }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <StyledTableCell>ลำดับ</StyledTableCell>
                <StyledTableCell>หัวข้อคำถาม</StyledTableCell>
                <StyledTableCell>สถานะ</StyledTableCell>
                <StyledTableCell>วันที่สร้าง</StyledTableCell>
                <StyledTableCell>วันที่แก้ไข</StyledTableCell>
                <StyledTableCell>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {faqList.map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell component="th" scope="row">
                    {row.order_no}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {row.question}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {row.is_publish ? "เผยแพร่" : "ไม่เผยแพร่"}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {row.created_date
                      ? dayjs(row.created_date)
                          .locale("th")
                          .format("DD MMM BBBB")
                      : "-"}
                  </StyledTableCell>
                  <StyledTableCell component="th" scope="row">
                    {row.last_updated_date
                      ? dayjs(row.last_updated_date)
                          .locale("th")
                          .format("DD MMM BBBB")
                      : "-"}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" gap="8px">
                      <Box
                        component="a"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleNavigateEditFaq(row.id)}
                      >
                        <ModeEditIcon />
                      </Box>
                      <Box
                        component="a"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleWarningOpen(row.id)}
                      >
                        <DeleteIcon />
                      </Box>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}

              {!faqList.length && (
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    scope="row"
                    colSpan={6}
                    align="center"
                  >
                    ไม่พบข้อมูล
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />

        {/* modals */}
        <Modal keepMounted open={openWarning} onClose={handleWarningClose}>
          <Box sx={modalStyle} width={340}>
            <DeleteModal
              handleConfirmDelete={handleConfirmDelete}
              handleWarningClose={handleWarningClose}
            />
          </Box>
        </Modal>
      </Box>
    </Box>
  );
};

export default FaqList;
